import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Camera, ClipboardCheck, Search, Eye, Trash2, Mail, Phone, Car, X } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../config';

const statusStyles = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  contacted: 'bg-blue-100 text-blue-800 border-blue-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200'
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  try {
    return new Date(value).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return value;
  }
};

const VehicleRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editStatus, setEditStatus] = useState('pending');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/vehicle-requests`);
      setRequests(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching vehicle requests:', error);
      toast.error('Failed to load vehicle requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const openDetail = (request) => {
    setSelected(request);
    setEditStatus(request.status || 'pending');
    setEditNotes(request.notes || '');
    setDetailOpen(true);
  };

  const handleSave = async () => {
    if (!selected) return;
    try {
      setSaving(true);
      await axios.patch(`${API}/vehicle-requests/${selected.id}`, {
        status: editStatus,
        notes: editNotes
      });
      setRequests((prev) =>
        prev.map((r) =>
          r.id === selected.id
            ? { ...r, status: editStatus, notes: editNotes, updated_at: new Date().toISOString() }
            : r
        )
      );
      toast.success('Vehicle request updated');
      setDetailOpen(false);
    } catch (error) {
      console.error('Error updating vehicle request:', error);
      toast.error('Failed to update request');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (request) => {
    if (!window.confirm(`Delete request from ${request.customer_name}? This cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/vehicle-requests/${request.id}`);
      setRequests((prev) => prev.filter((r) => r.id !== request.id));
      if (selected?.id === request.id) {
        setDetailOpen(false);
        setSelected(null);
      }
      toast.success('Request deleted');
    } catch (error) {
      console.error('Error deleting vehicle request:', error);
      toast.error('Failed to delete request');
    }
  };

  const filtered = requests.filter((r) => {
    if (typeFilter !== 'all' && r.request_type !== typeFilter) return false;
    if (statusFilter !== 'all' && (r.status || 'pending') !== statusFilter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const haystack = [
        r.customer_name,
        r.customer_email,
        r.customer_phone,
        r.vehicle_info?.stock_no,
        r.vehicle_info?.make,
        r.vehicle_info?.model,
        r.message
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Vehicle Requests</h1>
          <p className="text-gray-600 mt-1">
            Photo and inspection requests submitted from the public site
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            {requests.length} total
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search name, email, stock no..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="all">All Types</option>
            <option value="photos">Photos</option>
            <option value="inspection">Inspection</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="contacted">Contacted</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading requests...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            {requests.length === 0
              ? 'No vehicle requests yet.'
              : 'No requests match your filters.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-4 text-xs font-semibold text-gray-600 uppercase">Customer</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Vehicle</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Type</th>
                  <th className="text-left text-sm font-semibold text-gray-700">Details</th>
                  <th className="text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="text-left text-sm font-semibold text-gray-700">Submitted</th>
                  <th className="text-right text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y">
                {filtered.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {request.customer_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {request.customer_email}
                      </div>
                      <div className="text-xs text-gray-500">
                        {request.customer_phone}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900">
                        {request.vehicle_info?.year} {request.vehicle_info?.make}{' '}
                        {request.vehicle_info?.model}
                      </div>
                      <div className="text-xs text-gray-500">
                        Stock: {request.vehicle_info?.stock_no || 'N/A'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        variant="outline"
                        className={
                          request.request_type === 'photos'
                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                            : 'bg-green-50 text-green-700 border-green-200'
                        }
                      >
                        {request.request_type === 'photos' ? 'Photos' : 'Inspection'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {request.request_type === 'photos'
                        ? request.photo_type
                        : request.inspection_type}
                      {request.preferred_date && (
                        <div className="text-xs text-gray-500">
                          {request.preferred_date}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        variant="outline"
                        className={statusStyles[request.status] || 'bg-gray-100 text-gray-700 border-gray-200'}
                      >
                        {request.status || 'pending'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(request.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDetail(request)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      {detailOpen && selected && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setDetailOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
              <div className="flex items-center gap-3">
                <div
                  className={
                    'p-2 rounded-lg ' +
                    (selected.request_type === 'photos'
                      ? 'bg-blue-100'
                      : 'bg-green-100')
                  }
                >
                  {selected.request_type === 'photos' ? (
                    <Camera className="h-5 w-5 text-blue-600" />
                  ) : (
                    <ClipboardCheck className="h-5 w-5 text-green-600" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 capitalize">
                    {selected.request_type} Request
                  </h2>
                  <p className="text-sm text-gray-500">{formatDate(selected.created_at)}</p>
                </div>
              </div>
              <button onClick={() => setDetailOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              {/* Customer */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                    Customer
                  </h3>
                  <p className="font-medium text-gray-900">{selected.customer_name}</p>
                  <p className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                    <Mail className="h-3.5 w-3.5" /> {selected.customer_email}
                  </p>
                  <p className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                    <Phone className="h-3.5 w-3.5" /> {selected.customer_phone}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                    Vehicle
                  </h3>
                  <p className="font-medium text-gray-900 flex items-center gap-2">
                    <Car className="h-4 w-4 text-gray-400" />
                    {selected.vehicle_info?.year} {selected.vehicle_info?.make}{' '}
                    {selected.vehicle_info?.model}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Stock No: {selected.vehicle_info?.stock_no || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Request details */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                  Request Details
                </h3>
                <p className="text-sm text-gray-700">
                  <span className="text-gray-500">Type: </span>
                  {selected.request_type === 'photos' ? 'Photos' : 'Inspection'}
                </p>
                {selected.request_type === 'photos' && selected.photo_type && (
                  <p className="text-sm text-gray-700">
                    <span className="text-gray-500">Photo Type: </span>
                    {selected.photo_type}
                  </p>
                )}
                {selected.request_type === 'inspection' && selected.inspection_type && (
                  <p className="text-sm text-gray-700">
                    <span className="text-gray-500">Inspection Type: </span>
                    {selected.inspection_type}
                  </p>
                )}
                {selected.preferred_date && (
                  <p className="text-sm text-gray-700">
                    <span className="text-gray-500">Preferred Date: </span>
                    {selected.preferred_date}
                  </p>
                )}
                {selected.message && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">Message:</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {selected.message}
                    </p>
                  </div>
                )}
              </div>

              {/* Status + notes */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="pending">Pending</option>
                    <option value="contacted">Contacted</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Internal Notes
                  </label>
                  <Textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="Add a note about how this request was handled..."
                    rows={3}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <button
                  onClick={() => handleDelete(selected)}
                  className="inline-flex items-center gap-2 text-sm text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setDetailOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleRequests;
